import { ReactElement, useState, useEffect } from "react";
import { Layout, Form, Input, Button, Row, Col, Upload, message, Avatar, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Paper from "@material-ui/core/Paper";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";
import firebase from "firebase/app";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";
import { getAuthState } from "../../redux/selectors";
import { UPDATE_LOGIN_USER } from "../../redux/constants";
import Loading from "../../components/Loading";

const { Content } = Layout;
const { Option } = Select;

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 24 },
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function Profile(): ReactElement {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(getAuthState);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [gender, setGender] = useState<string | undefined>(currentUser?.gender);
  const { t } = useTranslation();

  useEffect(() => {
    firebase.auth().useDeviceLanguage();
    firebase.firestore().collection('users').doc(currentUser?.id).onSnapshot((snapshot) => {
      const data = snapshot.data();
      dispatch({
        type: UPDATE_LOGIN_USER,
        payload: { userInfo: data },
      });
    })
  }, [])

  const renderRecaptcha = (): any => {
    const recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      "recaptcha_container",
      {
        size: "invisible",
        callback: (response: any) => {
          console.log(response);
        },
      }
    );
    return recaptchaVerifier;
  };

  const onFinish = async (values: any) => {
    const profile = values;
    setLoading(true);
    if (values.photoUrl) {
      const imageAsFile = values.photoUrl.file;
      const ref = `/avatars/${currentUser?.id}/${imageAsFile.name}`;
      await firebase.storage().ref(ref).put(imageAsFile);
      const newAvataUrl = await firebase.storage().ref(ref).getDownloadURL();
      profile.photoUrl = newAvataUrl;
    } else {
      profile.photoUrl = currentUser?.photoUrl;
    }

    profile.gender = gender;

    Object.keys(profile).forEach((key) => {
      if (profile[key] === undefined) profile[key] = "";
    });


    if (currentUser?.phoneNumber !== profile.phoneNumber) {
      // Need to verify phone number
      const providerId = firebase.auth().currentUser?.providerData[0]?.providerId;

      if (providerId === "phone") {
        // verify phone number
        const appVerifier = renderRecaptcha();
        const provider = new firebase.auth.PhoneAuthProvider();
        provider.verifyPhoneNumber(profile.phoneNumber, appVerifier)
          .then(vertificationId => {
            const verificationCode = window.prompt("Please enter the verification code that was sent to your mobile device.");
            if (verificationCode) {
              return firebase.auth.PhoneAuthProvider.credential(vertificationId, verificationCode);
            }
            return null;
          })
          .then(phoneCredential => {
            if (phoneCredential)
              firebase.auth().currentUser?.updatePhoneNumber(phoneCredential)
                .then(() => {
                  firebase
                    .firestore()
                    .collection("users")
                    .doc(currentUser?.id)
                    .update(profile)
                    .then(() => {
                      dispatch({
                        type: UPDATE_LOGIN_USER,
                        payload: { userInfo: profile },
                      });
                      message.success("Your profile has been updated!");
                      setLoading(false);
                    })
                })
          })
          .catch((error) => {
            console.log(error);
            message.error("Sorry, we can not process your request now, maybe your new phone number is not valild or was used in other account. please try again later!");
            setLoading(false);
          })
      }
      else {
        // check if there is already same phone number
        const snapshot = await firebase.firestore().collection("users").where("phoneNumber", "==", profile.phoneNumber).get();
        if (snapshot.size) {
          message.error("New phone number is already used in other account. please use another phone number!");
          // return;
        } else {
          firebase
            .firestore()
            .collection("users")
            .doc(currentUser?.id)
            .update(profile)
            .then(() => {
              dispatch({
                type: UPDATE_LOGIN_USER,
                payload: { userInfo: profile },
              });
              message.success("Your profile has been updated!");
              setLoading(false);
            })
            .catch(e => {
              message.error("Oop! Something went wrong!");
              setLoading(false);
            })
        }
      }
    } else {
      firebase
        .firestore()
        .collection("users")
        .doc(currentUser?.id)
        .update(profile)
        .then(() => {
          dispatch({
            type: UPDATE_LOGIN_USER,
            payload: { userInfo: profile },
          });
          setLoading(false);
          message.success("Your profile has been updated!");
        })
        .catch(e => {
          console.log(e);
          setLoading(false);
          message.error("Oop! Something went wrong!");
        })
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const handleAvatarChange = (info: any) => {
    setSelectedFile(info.file.name);
    if (info.file.status === "uploading") {
      setLoading(true);
      return;
    }
    if (info.file.status === "done") {
      // eslint-disable-next-line no-console
      console.log("done");
    }
  };

  return (
    <Content>
      <Row>
        <Col sm={24} md={{ span: 12, offset: 6 }}>
          <Paper style={{ padding: 20 }}>
            <Form
              {...layout}
              name="basic"
              initialValues={{
                fullName: currentUser?.fullName,
                email: currentUser?.email,
                instagram: currentUser?.instagram,
                phoneNumber: currentUser?.phoneNumber,
              }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <Form.Item label="Avatar" name="photoUrl">
                <Avatar
                  src={currentUser?.photoUrl}
                  size={30}
                  icon={<UserOutlined />}
                />
                <Upload
                  name="avatar"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={handleAvatarChange}
                >
                  <Button icon={<UploadOutlined />} style={{ marginLeft: 5 }}>
                    {t("common.update")}
                  </Button>
                </Upload>
                {selectedFile && (
                  <span style={{ marginLeft: 10 }}>{selectedFile}</span>
                )}
              </Form.Item>
              <Form.Item label={t("profile.full_name")} name="fullName">
                <Input />
              </Form.Item>
              <Form.Item label={t("profile.email")} name="email">
                <Input disabled />
              </Form.Item>
              <Form.Item label={t("profile.gender")} name="gender">
                <Select
                  placeholder={t("profile.gender_placeholder")}
                  defaultValue={gender}
                  onChange={(value) => setGender(value)}
                >
                  <Option value="male">{t("profile.male")}</Option>
                  <Option value="female">{t("profile.female")}</Option>
                </Select>
              </Form.Item>
              <Form.Item label={t("profile.instagram")} name="instagram">
                <Input />
              </Form.Item>
              <Form.Item label={t("profile.phone_number")} name="phoneNumber">
                <PhoneInput
                  inputStyle={{ width: "100%", height: "36px" }}
                  onlyCountries={["eg", "bh", "qa", "sa", "kw", "ae"]}
                />
              </Form.Item>
              <div style={{ textAlign: "center" }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t("common.update")}
                </Button>
              </div>
            </Form>
          </Paper>
        </Col>
      </Row>
      <div id="recaptcha_container" />
      {loading && <Loading />}
    </Content>
  );
}
